import {AriaLive} from '@wandb/components/AriaLive';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useMemo, useRef} from 'react';
import {Popup} from 'semantic-ui-react';

import {trackRunVisibilityMenu} from '../../../services/analytics/runSelectAllVisibilityMenuEvents';
import type {Ref} from '../../../state/views/filter/types';
import type {DefaultToggleFilter} from '../../../util/filterTypes';
import {useRampFlagWorkspaceFilterVisibilityUI} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {type Config as TableSettings} from '../../../util/runfeed';
import {useRunSelectorAnalyticsLocation} from '../RunSelectorAnalyticsLocationContext';
import {useRunSelectorFeatureEnabled} from '../RunSelectorFeaturesContext';
import {WBTableActionFilter} from '../WBTable/WBTableActionsFilter';
import {RunsFilterAgentSlices, RunsFilterButton} from './RunsFilterButton';
import {RunsFilterKeySelector} from './RunsFilterKeySelector';
import {RunsFilterPicker} from './RunsFilterPicker';
import {RunsFilterValueSelector} from './RunsFilterValueSelector';

const NOTIFICATION_TEXT = 'New filter applied';

interface RunsFilterTableActionProps {
  entityName: string;
  projectName: string;
  tableSettings: TableSettings;
  compact?: boolean;
  filtersRef: Ref;
  notificationOpen: boolean;
  pickerOpen: boolean;

  defaultToggleFilters?: DefaultToggleFilter[];
  setOnlyShowSelected?: (newSetting: boolean) => void;
  setPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onFiltersChanged(): void;
}

export const RunsFilterTableAction = ({
  compact,
  defaultToggleFilters,
  entityName,
  filtersRef,
  notificationOpen,
  onFiltersChanged,
  pickerOpen,
  projectName,
  setOnlyShowSelected,
  setPickerOpen,
  tableSettings,
}: RunsFilterTableActionProps) => {
  const isFilterVisibilityUIEnabled = useRampFlagWorkspaceFilterVisibilityUI();
  const isEnabled = useRunSelectorFeatureEnabled();
  const isCompact = isFilterVisibilityUIEnabled ? false : compact;
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const buttonAnchorRef = useRef<HTMLDivElement>(null);

  const onlyShowSelected = Boolean(tableSettings.onlyShowSelected);
  const hideManuallyHidden = useMemo(
    () =>
      isEnabled('toggle-visibility') &&
      isFilterVisibilityUIEnabled &&
      setOnlyShowSelected
        ? {
            checked: onlyShowSelected,
            onChange: (checked: boolean) => {
              setOnlyShowSelected(checked);
              // event name kept as 'Only show visualized toggled' despite the
              // renamed UI label ('List only visible runs') for analytic continuity
              trackRunVisibilityMenu({
                action: 'Only show visualized toggled',
                method: 'filter_modal',
                location: analyticsLocation,
              });
            },
          }
        : undefined,
    [
      isFilterVisibilityUIEnabled,
      onlyShowSelected,
      setOnlyShowSelected,
      analyticsLocation,
      isEnabled,
    ]
  );

  const extraFilterCount =
    isFilterVisibilityUIEnabled && onlyShowSelected ? 1 : 0;

  return (
    // Shrink-wraps the button so its rect can anchor the notification popup.
    // Styled inline rather than with Tailwind so the button subtree stays out
    // of `tw-style`, and so keeps the preflight reset its sibling table actions
    // also do without.
    <div ref={buttonAnchorRef} style={{display: 'inline-flex'}}>
      <RunsFilterAgentSlices filtersRef={filtersRef} />
      <WBTableActionFilter
        open={pickerOpen}
        setOpen={setPickerOpen}
        trigger={filtersOpen => (
          <RunsFilterButton
            compact={isCompact}
            filtersOpen={filtersOpen}
            filtersRef={filtersRef}
            extraFilterCount={extraFilterCount}
          />
        )}
        content={
          <RunsFilterPicker
            entityName={entityName}
            projectName={projectName}
            filtersRef={filtersRef}
            defaultToggleFilters={defaultToggleFilters}
            hideManuallyHidden={hideManuallyHidden}
            onAgentChatOpened={() => setPickerOpen(false)}
            onFiltersChanged={onFiltersChanged}
            filterKeySelector={filterKeySelectorProps => (
              <RunsFilterKeySelector
                {...filterKeySelectorProps}
                entityName={entityName}
                projectName={projectName}
                tableSettings={tableSettings}
              />
            )}
            filterValueSelector={filterValueSelectorProps => {
              return (
                <RunsFilterValueSelector
                  {...filterValueSelectorProps}
                  entityName={entityName}
                  projectName={projectName}
                />
              );
            }}
          />
        }
      />
      <TailwindContents>
        <AriaLive>{notificationOpen ? NOTIFICATION_TEXT : ''}</AriaLive>
      </TailwindContents>
      {/* Still the semantic-ui popup, unchanged apart from being bound to the
          wrapper via `context` instead of wrapping the button as its trigger —
          the button now belongs to Dialog.Trigger. It stays a Popup because it
          has to be portalled out of `.wb-table-actions`, whose `overflow-x:
          auto` makes `overflow-y` compute to `auto` and would scroll anything
          positioned below the toolbar out of sight. Announced by the AriaLive
          region above, so hidden from the accessibility tree here to avoid
          saying it twice. */}
      <Popup
        aria-hidden
        open={notificationOpen}
        context={buttonAnchorRef}
        position="bottom left"
        basic
        pinned
        inverted
        on="click"
        style={{top: 24, marginLeft: 1, padding: '4px 8px'}}
        content={NOTIFICATION_TEXT}
      />
    </div>
  );
};
